import { computed, defineComponent, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { Popover } from 'bootstrap';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ProjectAction, ProjectGetter } from '@/store/project/project.module';
import { Project } from '@/store/project/project.model';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { Province } from '@/store/province/province.model';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'ProjectTableComponent',
  components: {
    LoadingComponent,
  },
  props: {
    fiscalYear: {
      type: Object as PropType<FiscalYear>
    },
    province: {
      type: Object as PropType<Province>
    },
    budgetTypeId: {
      type: Number,
      default: 0
    }
  },
  setup: (props) => {
    const store = useStore();
    const router = useRouter();
    const projects = computed<Project[]>(() => {
      const all = store.getters[ProjectGetter.ALL] as Project[];

      return props.budgetTypeId !== 0
        ? all.filter(m => m.projectBudgets.some(x => x.id === props.budgetTypeId))
        : all;
    });
    const isLoading = computed(() => store.getters[ProjectGetter.STATUS] === 'init' || store.getters[ProjectGetter.STATUS] === 'loading');
    const datatable = ref<HTMLTableElement>();
    const dtInstance = ref();
    const popoverList = ref<Popover[]>([]);

    onMounted(() => {
      if (props.fiscalYear && props.province) {
        updateDataTable(props.fiscalYear.id, props.province.id);
      }
    });

    onUnmounted(() => {
      dtInstance.value?.destroy();
    });

    watch(() => ([props.fiscalYear, props.province]), ([fiscalYear, province]) => {
      if (fiscalYear && province) {
        updateDataTable(fiscalYear.id, province.id);
      }
    });

    watch(() => props.budgetTypeId, () => {
      if (props.fiscalYear && props.province) {
        updateDataTable(props.fiscalYear.id, props.province.id);
      }
    });

    const updateDataTable = (fiscalYearId: number, provinceId: number) => {
      Promise.resolve(dtInstance.value?.destroy())
        .then(() => {
          store.dispatch(ProjectAction.FETCH, {
            fiscalYearId: fiscalYearId,
            appointment: 'Provinces',
            appointmentId: provinceId
          })
          .then(() => initialPopover())
          .then(() => initialDataTable());
        });
    };

    const initialPopover = () => {
      const popoverTriggerList = computed<HTMLElement[]>(() => [].slice.call(document.querySelectorAll('[data-bs-toggle="popover-project"]')) as HTMLElement[]);
      popoverList.value.map(element => element.hide());
      popoverList.value = popoverTriggerList.value?.map(element => new Popover(element, { trigger: 'hover', placement: 'right' }));
    };

    const initialDataTable = () => {
      const dt = datatable.value as HTMLTableElement;
      if (!$.fn.dataTable.isDataTable(dt)) {
        dtInstance.value = $(dt).DataTable({
          columnDefs: [
            { type: 'formatted-num', targets: 2 },
            { type: 'formatted-num', targets: 3 },
            { type: 'formatted-num', targets: 4 },
          ],
          language: {
            emptyTable: 'ไม่พบข้อมูล',
            info: 'แสดง _START_ ถึง _END_ จากจำนวนทั้งหมด _TOTAL_',
            infoEmpty: 'แสดง 0 ถึง 0 จากจำนวนทั้งหมด 0',
            infoFiltered: '(ค้นหาจากจำนวนทั้งหมด _MAX_)',
            lengthMenu: 'แสดงข้อมูล _MENU_',
            paginate: {
              next: 'ถัดไป',
              previous: 'ก่อนหน้า',
              first: 'First',
              last: 'Last'
            },
            search: 'ค้นหา:',
            zeroRecords: 'ไม่พบข้อมูลที่ค้นหา',
          }
        });
      }
    };

    const selectedClick = (projectId: number) => {
      Promise.resolve(popoverList.value.map(element => element.hide()))
        .then(() => router.push(`/project/${projectId}`));
    };

    const status = (isCanceled: boolean, name: string, color: string) => {
      if (isCanceled) {
        return '<span class="text-danger">ยกเลิก</span>';
      }

      return `<span style="color: ${color}">${name}</span>`;
    };

    return {
      datatable,
      projects,
      isLoading,
      selectedClick,
      status,
    };
  }
});
